/**
 * ValidatedTextarea - Auto-integrated Textarea Field
 *
 * Smart textarea component that automatically integrates with react-hook-form context.
 * Includes built-in validation, character counter, error display, and helper text.
 *
 * Features:
 * - Automatic react-hook-form registration
 * - Character counter (optional)
 * - Inline error display with icons
 * - Helper text support
 * - Semantic color tokens
 * - Resizable with constraints
 *
 * @module validated-textarea
 */

'use client';

import * as React from 'react';
import { useFormContext, type RegisterOptions } from 'react-hook-form';
import { LuTriangleAlert, LuCircleHelp } from 'react-icons/lu';

import { Textarea, type TextareaProps } from '../../textarea';
import { Field } from '../../form'; // Use Chakra UI Field for SSR-safe ID generation
import { Box } from '../../box';
import { Text } from '../../text';
import { HStack, VStack } from '../../stack';
import { Tooltip } from '../../ui/tooltip';

// ============================================================================
// Types
// ============================================================================

/**
 * ValidatedTextarea props
 */
export interface ValidatedTextareaProps extends Omit<TextareaProps, 'name'> {
  /**
   * Field name (required for react-hook-form)
   */
  name: string;

  /**
   * Field label
   */
  label?: string;

  /**
   * Helper text (shown below textarea)
   */
  helperText?: string;

  /**
   * Tooltip text (shown next to label with info icon)
   */
  tooltipText?: string;

  /**
   * Show character counter
   * @default true (if maxLength is provided)
   */
  showCharacterCount?: boolean;

  /**
   * Validation rules (react-hook-form)
   */
  rules?: RegisterOptions;

  /**
   * Whether field is required
   * @default false
   */
  required?: boolean;
}

// ============================================================================
// Component
// ============================================================================

/**
 * ValidatedTextarea - Smart textarea with automatic validation
 *
 * Automatically integrates with react-hook-form context and provides
 * comprehensive validation feedback.
 *
 * @example Basic usage
 * ```tsx
 * <FormProvider {...methods}>
 *   <ValidatedTextarea
 *     name="description"
 *     label="Description"
 *     rows={4}
 *     required
 *     helperText="Provide a detailed description"
 *   />
 * </FormProvider>
 * ```
 *
 * @example With character counter
 * ```tsx
 * <ValidatedTextarea
 *   name="notes"
 *   label="Notes"
 *   maxLength={500}
 *   showCharacterCount
 *   rows={5}
 *   resize="vertical"
 *   tooltipText="Additional information or special instructions"
 * />
 * ```
 *
 * @example With custom validation
 * ```tsx
 * <ValidatedTextarea
 *   name="comments"
 *   label="Comments"
 *   rules={{
 *     required: 'Please provide comments',
 *     minLength: { value: 10, message: 'Minimum 10 characters required' },
 *   }}
 *   rows={3}
 * />
 * ```
 */
export const ValidatedTextarea = React.forwardRef<HTMLTextAreaElement, ValidatedTextareaProps>(
  (
    {
      name,
      label,
      helperText,
      tooltipText,
      showCharacterCount,
      rules,
      required = false,
      maxLength,
      rows = 3,
      resize = 'vertical',
      ...textareaProps
    },
    ref
  ) => {
    // ============================================================================
    // Form Context
    // ============================================================================

    const formContext = useFormContext();
    const {
      register,
      formState: { errors },
      watch,
    } = formContext || {};

    // Defensive: If no form context, render basic textarea
    if (!formContext) {
      return (
        <VStack gap={2} align="stretch">
          {label && (
            <Text fontSize="sm" fontWeight="medium" color="fg.default">
              {label}
              {required && (
                <Text as="span" color="fg.error" marginLeft={1}>
                  *
                </Text>
              )}
            </Text>
          )}
          <Textarea
            ref={ref}
            name={name}
            required={required}
            maxLength={maxLength}
            rows={rows}
            resize={resize}
            {...textareaProps}
          />
          {helperText && (
            <Text fontSize="xs" color="fg.subtle">
              {helperText}
            </Text>
          )}
        </VStack>
      );
    }

    // ============================================================================
    // Derived State
    // ============================================================================

    const error = errors?.[name];
    const value = watch?.(name) || '';
    const valueLength = String(value).length;

    const shouldShowCharacterCount =
      showCharacterCount !== false && maxLength !== undefined && maxLength > 0;

    // Warning threshold (90% of maxLength)
    const warningThreshold = maxLength ? maxLength * 0.9 : 0;
    const isNearLimit = maxLength && valueLength > warningThreshold;

    // ============================================================================
    // Registration
    // ============================================================================

    const registration = register(name, {
      required: required ? `${label || name} is required` : false,
      maxLength: maxLength
        ? {
            value: maxLength,
            message: `Maximum ${maxLength} characters allowed`,
          }
        : undefined,
      ...rules,
    });

    // ============================================================================
    // Render
    // ============================================================================

    return (
      <Field.Root invalid={!!error} required={required}>
        {/* Label Row */}
        {label && (
          <HStack justifyContent="space-between" marginBottom={2}>
            <Box as={Field.Label}>
              <HStack gap={1} align="center">
                <Text fontSize="sm" fontWeight="medium" color="fg.default">
                  {label}
                </Text>
                {required && (
                  <Text fontSize="sm" color="fg.error">
                    *
                  </Text>
                )}
                {tooltipText && (
                  <Tooltip content={tooltipText}>
                    <Box as="span" display="inline-flex" cursor="help">
                      <LuCircleHelp size={14} color="var(--chakra-colors-fg-muted)" />
                    </Box>
                  </Tooltip>
                )}
              </HStack>
            </Box>

            {/* Character Counter */}
            {shouldShowCharacterCount && (
              <Text
                fontSize="xs"
                color={isNearLimit ? 'orange.500' : 'fg.muted'}
                fontWeight={isNearLimit ? 'medium' : 'normal'}
              >
                {valueLength}/{maxLength}
              </Text>
            )}
          </HStack>
        )}

        {/* Textarea Field */}
        <Textarea
          {...registration}
          {...textareaProps}
          ref={ref}
          maxLength={maxLength}
          rows={rows}
          resize={resize}
          aria-invalid={!!error}
          aria-describedby={error ? `${name}-error` : helperText ? `${name}-helper` : undefined}
        />

        {/* Error / Helper Text */}
        <VStack gap={1} align="flex-start" marginTop={1}>
          {error && (
            <HStack gap={1}>
              <LuTriangleAlert size={12} color="var(--chakra-colors-fg-error)" />
              <Box as={Field.ErrorText} id={`${name}-error`} fontSize="sm">
                {error.message as string}
              </Box>
            </HStack>
          )}

          {helperText && !error && (
            <Box as={Field.HelperText} id={`${name}-helper`} fontSize="xs" color="fg.subtle">
              {helperText}
            </Box>
          )}
        </VStack>
      </Field.Root>
    );
  }
);

ValidatedTextarea.displayName = 'ValidatedTextarea';
